import {
  ShoppingBag
  // TrendingUp
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { getDateRange } from "@/lib/utils/date";
import { DateRange } from "@/types/enums";
import { TTopProductsReportRequest } from "@shoutout-labs/market_buzz_crm_types";
import { AnalyticsService } from "@/services/AnalyticsService";

export const TopProductCard = () => {
  const { data } = useQuery({
    queryKey: ["topProducts", DateRange.YESTERDAY],
    queryFn: () => {
      const { startDate, endDate } = getDateRange(DateRange.YESTERDAY);
      const request: TTopProductsReportRequest = {
        fromDate: startDate,
        toDate: endDate,
        numberOfProducts: 1
      };
      return AnalyticsService.getTopProductsAnalytics(request);
    }
  });
  const navigate = useNavigate();

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            onClick={() => navigate("/reports/sales/products")}
            className="group relative px-5 py-3.5 rounded-[18px] bg-gradient-to-br from-[#F0FBF8] via-white to-white backdrop-blur-sm border border-primary/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,150,136,0.12)] transition-all duration-300 hover:scale-[1.02] cursor-pointer overflow-hidden"
          >
            {/* Decorative background blur */}
            <div className="absolute inset-0 opacity-[0.03]">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-primary/30 blur-3xl" />
            </div>

            {/* Hover glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-[#FFD166]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="relative flex flex-col items-center text-center h-full">
              {/* Header */}
              <div className="flex items-center gap-2 mb-2.5 self-start">
                <ShoppingBag className="w-4 h-4 text-primary" />
                <h3 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">
                  Top Selling Product
                </h3>
              </div>

              {/* Product Display - Centered */}
              <div className="flex-1 flex flex-col items-center justify-center">
                {!data || data.length === 0 ? (
                  // No products view
                  <>
                    {/* Icon with glow */}
                    <div className="relative mb-3 animate-scale-in">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-[#FFD166]/20 rounded-full blur-xl scale-110" />
                      <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center shadow-lg">
                        <span className="text-5xl">📦</span>
                      </div>
                    </div>

                    {/* Encouraging message */}
                    <div className="space-y-2 text-center max-w-[200px]">
                      <p className="text-[15px] font-bold text-foreground leading-tight">
                        No Sales For Yesterday
                      </p>
                      <p className="text-[12px] text-muted-foreground leading-relaxed">
                        Your next sale is just around the corner — keep up the
                        great work!
                      </p>
                    </div>
                  </>
                ) : (
                  // Product exists view
                  <>
                    {/* Product image with glow */}
                    <div className="relative mb-2 animate-scale-in">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-[#FFD166]/20 rounded-full blur-xl scale-110" />
                      <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center shadow-lg">
                        <span className="text-5xl">📦</span>
                      </div>
                    </div>

                    {/* Product info */}
                    <div className="space-y-1">
                      <p className="text-[17px] font-bold text-foreground leading-tight">
                        {data[0]?.productName}
                      </p>
                      <p className="text-[13px] text-muted-foreground">
                        Most sold item yesterday —{" "}
                        <span className="font-semibold text-foreground">
                          {data[0]?.productsQuantity} orders
                        </span>
                      </p>

                      {/* Trend indicator */}
                      {/* <div className="flex items-center justify-center gap-1.5 pt-0.5">
                        <TrendingUp className="w-3.5 h-3.5 text-primary" />
                        <span className="text-[11px] font-medium text-primary">
                          +12% vs yesterday
                        </span>
                      </div> */}
                    </div>

                    {/* Mini sales bar */}
                    <div className="flex items-center gap-0.5 mt-2">
                      {[100, 85, 92, 78, 100, 88, 95].map((height, i) => (
                        <div
                          key={i}
                          className="w-1 bg-primary/30 rounded-full transition-all duration-300 group-hover:bg-primary/50"
                          style={{ height: `${height * 0.18}px` }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">View full product report</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
